// Admin-side data access for capability profiles.
//
// Wraps the three admin-guarded callables (listCapabilityProfiles,
// saveCapabilityProfile, deleteCapabilityProfile) with reactive state and
// user-facing error mapping. Nothing here is a security boundary — the
// functions enforce, this shapes what the admin sees.
//
// State is hoisted to module scope so both AdminView (which needs the profile
// list to render name pills on user rows and populate AssignProfilesDialog)
// and CapabilityProfilesPanel (which owns the CRUD UI) see the same reactive
// values. A create in the panel is immediately visible to AdminView without a
// separate refresh path. Standard singleton composable pattern — the
// alternative was passing an event bus, which is more code for the same
// effect.

import { httpsCallable, type FunctionsError } from 'firebase/functions'
import { ref } from 'vue'

import { functions } from '../firebase'
import type {
  CapabilityProfile,
  DeleteCapabilityProfileRequest,
  DeleteCapabilityProfileResponse,
  ListCapabilityProfilesResponse,
  SaveCapabilityProfileRequest,
  SaveCapabilityProfileResponse,
} from '../types/callables'

const callList = httpsCallable<void, ListCapabilityProfilesResponse>(
  functions,
  'listCapabilityProfiles',
)
const callSave = httpsCallable<SaveCapabilityProfileRequest, SaveCapabilityProfileResponse>(
  functions,
  'saveCapabilityProfile',
)
const callDelete = httpsCallable<DeleteCapabilityProfileRequest, DeleteCapabilityProfileResponse>(
  functions,
  'deleteCapabilityProfile',
)

/**
 * Result of a delete attempt. `in-use` carries the assignment count the
 * server refused on, so the UI can offer a forced-detach confirmation without
 * a second round-trip.
 */
export type DeleteResult =
  | { kind: 'ok'; detachedFrom: string[] }
  | { kind: 'in-use'; count: number }
  | { kind: 'error'; message: string }

function profileErrorMessage(e: unknown): string {
  const err = e as FunctionsError
  const msg = err?.message ?? ''
  const code = err?.code ?? ''

  if (msg.startsWith('capabilities-')) {
    return 'That capability set is malformed. Reload the page and try again.'
  }
  if (msg === 'name-required') return 'Give the profile a name.'
  if (msg === 'id-required')   return 'Missing profile id. Reload the list and try again.'
  if (code === 'functions/permission-denied') return 'You do not have permission to do that.'
  if (code === 'functions/unavailable')       return 'Could not reach the server. Try again.'
  return 'Something went wrong. Try again.'
}

/**
 * Pulls the numeric assignment count off a `profile-in-use:N` error message.
 * Returns null for any other error shape.
 */
function extractInUseCount(e: unknown): number | null {
  const msg = (e as FunctionsError)?.message ?? ''
  const match = /^profile-in-use:(\d+)$/.exec(msg)
  return match ? Number(match[1]) : null
}

// Module-scoped state so every caller shares one source of truth.
const profiles         = ref<CapabilityProfile[]>([])
const assignmentCounts = ref<Record<string, number>>({})
const loading          = ref(false)
const error            = ref<string | null>(null)

export function useCapabilityProfiles() {
  async function load(): Promise<void> {
    loading.value = true
    error.value   = null
    try {
      const { data } = await callList()
      profiles.value         = data.profiles
      assignmentCounts.value = data.assignmentCounts
    } catch (e) {
      error.value = profileErrorMessage(e)
    } finally {
      loading.value = false
    }
  }

  async function save(req: SaveCapabilityProfileRequest): Promise<string | null> {
    error.value = null
    try {
      const { data } = await callSave(req)
      await load()
      return data.id
    } catch (e) {
      error.value = profileErrorMessage(e)
      return null
    }
  }

  async function remove(id: string, force = false): Promise<DeleteResult> {
    error.value = null
    try {
      // Omit `force` when false — the callable protocol serialises an explicit
      // false into the payload just fine, but keeping the payload tight makes
      // server logs readable when debugging.
      const req: DeleteCapabilityProfileRequest = force ? { id, force: true } : { id }
      const { data } = await callDelete(req)
      await load()
      return { kind: 'ok', detachedFrom: data.detachedFrom ?? [] }
    } catch (e) {
      const inUse = extractInUseCount(e)
      if (inUse !== null) return { kind: 'in-use', count: inUse }
      const message = profileErrorMessage(e)
      error.value = message
      return { kind: 'error', message }
    }
  }

  return { profiles, assignmentCounts, loading, error, load, save, remove }
}
